// src/index.tsx
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  CopyIcon,
  LockIcon,
  UnlockIcon
} from "@chakra-ui/icons";
import * as React6 from "react";
import { getCompatContextProvider } from "react-querybuilder";

// src/ChakraActionElement.tsx
import { Button } from "@chakra-ui/react";
import * as React from "react";
var ChakraActionElement = ({
  className,
  handleOnClick,
  label,
  title,
  disabled,
  disabledTranslation,
  // Props that should not be in extraProps
  testID: _testID,
  rules: _rules,
  level: _level,
  path: _path,
  context: _context,
  validation: _validation,
  ruleOrGroup: _ruleOrGroup,
  schema: _schema,
  ...extraProps
}) => /* @__PURE__ */ React.createElement(
  Button,
  {
    className,
    title: disabledTranslation && disabled ? disabledTranslation.title : title,
    onClick: (e) => handleOnClick(e),
    isDisabled: disabled && !disabledTranslation,
    ...extraProps
  },
  disabledTranslation && disabled ? disabledTranslation.label : label
);

// src/ChakraDragHandle.tsx
import { DragHandleIcon } from "@chakra-ui/icons";
import { IconButton } from "@chakra-ui/react";
import * as React2 from "react";
import { forwardRef } from "react";
var ChakraDragHandle = forwardRef(
  ({
    className,
    title,
    disabled,
    // Props that should not be in extraProps
    testID: _testID,
    level: _level,
    path: _path,
    label: _label,
    context: _context,
    validation: _validation,
    schema: _schema,
    ruleOrGroup: _ruleOrGroup,
    ...extraProps
  }, dragRef) => /* @__PURE__ */ React2.createElement("span", { ref: dragRef, className, title }, /* @__PURE__ */ React2.createElement(
    IconButton,
    {
      isDisabled: disabled,
      icon: /* @__PURE__ */ React2.createElement(DragHandleIcon, null),
      "aria-label": title ?? /* istanbul ignore next */
      "",
      ...extraProps
    }
  ))
);

// src/ChakraNotToggle.tsx
import { FormControl, FormLabel, Switch } from "@chakra-ui/react";
import * as React3 from "react";
import { useId } from "react";
var ChakraNotToggle = ({
  className,
  handleOnChange,
  label,
  checked,
  title,
  disabled,
  // Props that should not be in extraProps
  path: _path,
  context: _context,
  validation: _validation,
  testID: _testID,
  schema: _schema,
  ruleGroup: _ruleGroup,
  ...extraProps
}) => {
  const id = useId();
  return /* @__PURE__ */ React3.createElement("div", { style: { display: "inline-block" } }, /* @__PURE__ */ React3.createElement(
    FormControl,
    {
      display: "flex",
      alignItems: "center",
      className,
      title,
      isDisabled: disabled
    },
    /* @__PURE__ */ React3.createElement(
      Switch,
      {
        id,
        isChecked: checked,
        isDisabled: disabled,
        onChange: (e) => handleOnChange(e.target.checked),
        ...extraProps
      }
    ),
    /* @__PURE__ */ React3.createElement(FormLabel, { htmlFor: id, marginBottom: 0 }, label)
  ));
};

// src/ChakraValueEditor.tsx
import { Checkbox, Input, Radio, RadioGroup, Stack, Switch as Switch2, Textarea } from "@chakra-ui/react";
import * as React4 from "react";
import { ValueSelector, getFirstOption, useValueEditor } from "react-querybuilder";
var ChakraValueEditor = (allProps) => {
  const {
    fieldData,
    operator,
    value,
    handleOnChange,
    title,
    className,
    type,
    inputType,
    values = [],
    listsAsArrays,
    separator,
    valueSource: _vs,
    testID,
    disabled,
    selectorComponent: SelectorComponent = allProps.schema.controls.valueSelector,
    extraProps,
    ...props
  } = allProps;
  const { valueAsArray, multiValueHandler, valueListItemClassName } = useValueEditor(allProps);
  if (operator === "null" || operator === "notNull") {
    return null;
  }
  const placeHolderText = fieldData?.placeholder ?? "";
  const inputTypeCoerced = ["in", "notIn"].includes(operator) ? "text" : inputType || "text";
  if ((operator === "between" || operator === "notBetween") && (type === "select" || type === "text")) {
    const editors = ["from", "to"].map((key, i) => {
      if (type === "text") {
        return /* @__PURE__ */ React4.createElement(
          Input,
          {
            key,
            type: inputTypeCoerced,
            value: valueAsArray[i] ?? "",
            isDisabled: disabled,
            className: valueListItemClassName,
            placeholder: placeHolderText,
            onChange: (e) => multiValueHandler(e.target.value, i),
            ...extraProps
          }
        );
      }
      return /* @__PURE__ */ React4.createElement(
        SelectorComponent,
        {
          key,
          ...props,
          className: valueListItemClassName,
          handleOnChange: (v) => multiValueHandler(v, i),
          disabled,
          value: valueAsArray[i] ?? getFirstOption(values),
          options: values,
          listsAsArrays
        }
      );
    });
    return /* @__PURE__ */ React4.createElement("span", { "data-testid": testID, className, title }, editors[0], separator, editors[1]);
  }
  switch (type) {
    case "select":
      return /* @__PURE__ */ React4.createElement(
        SelectorComponent,
        {
          ...props,
          className,
          title,
          value,
          disabled,
          handleOnChange,
          options: values
        }
      );
    case "multiselect":
      return /* @__PURE__ */ React4.createElement(
        ValueSelector,
        {
          ...props,
          className,
          title,
          value,
          disabled,
          handleOnChange,
          options: values,
          multiple: true,
          listsAsArrays
        }
      );
    case "textarea":
      return /* @__PURE__ */ React4.createElement(
        Textarea,
        {
          value,
          title,
          isDisabled: disabled,
          className,
          placeholder: placeHolderText,
          onChange: (e) => handleOnChange(e.target.value),
          ...extraProps
        }
      );
    case "switch":
      return /* @__PURE__ */ React4.createElement(
        Switch2,
        {
          className,
          isChecked: !!value,
          title,
          isDisabled: disabled,
          onChange: (e) => handleOnChange(e.target.checked),
          ...extraProps
        }
      );
    case "checkbox":
      return /* @__PURE__ */ React4.createElement(
        Checkbox,
        {
          className,
          title,
          isDisabled: disabled,
          onChange: (e) => handleOnChange(e.target.checked),
          isChecked: !!value,
          ...extraProps
        }
      );
    case "radio":
      return /* @__PURE__ */ React4.createElement(
        RadioGroup,
        {
          className,
          title,
          value,
          onChange: handleOnChange,
          isDisabled: disabled,
          ...extraProps
        },
        /* @__PURE__ */ React4.createElement(Stack, { direction: "row" }, values.map((v) => /* @__PURE__ */ React4.createElement(Radio, { key: v.name, value: v.name }, v.label)))
      );
  }
  return /* @__PURE__ */ React4.createElement(
    Input,
    {
      type: inputTypeCoerced,
      value,
      title,
      isDisabled: disabled,
      className,
      placeholder: placeHolderText,
      onChange: (e) => handleOnChange(e.target.value),
      ...extraProps
    }
  );
};

// src/ChakraValueSelector.tsx
import { Select } from "@chakra-ui/react";
import * as React5 from "react";

// src/utils.ts
import { isOptionGroupArray, toOptions } from "react-querybuilder";

// src/ChakraValueSelector.tsx
var ChakraValueSelector = ({
  className,
  handleOnChange,
  options,
  value,
  title,
  disabled,
  // Props that should not be in extraProps
  testID: _testID,
  rule: _rule,
  rules: _rules,
  level: _level,
  path: _path,
  context: _context,
  validation: _validation,
  operator: _operator,
  field: _field,
  fieldData: _fieldData,
  multiple: _multiple,
  listsAsArrays: _listsAsArrays,
  schema: _schema,
  ...extraProps
}) => /* @__PURE__ */ React5.createElement(
  Select,
  {
    className,
    title,
    value,
    disabled,
    onChange: (e) => handleOnChange(e.target.value),
    ...extraProps
  },
  toOptions(options)
);

// src/index.tsx
var chakraControlElements = {
  actionElement: ChakraActionElement,
  valueSelector: ChakraValueSelector,
  dragHandle: ChakraDragHandle,
  notToggle: ChakraNotToggle,
  valueEditor: ChakraValueEditor
};
var chakraTranslations = {
  removeGroup: { label: /* @__PURE__ */ React6.createElement(CloseIcon, null) },
  removeRule: { label: /* @__PURE__ */ React6.createElement(CloseIcon, null) },
  cloneRuleGroup: { label: /* @__PURE__ */ React6.createElement(CopyIcon, null) },
  cloneRule: { label: /* @__PURE__ */ React6.createElement(CopyIcon, null) },
  lockGroup: { label: /* @__PURE__ */ React6.createElement(UnlockIcon, null) },
  lockRule: { label: /* @__PURE__ */ React6.createElement(UnlockIcon, null) },
  lockGroupDisabled: { label: /* @__PURE__ */ React6.createElement(LockIcon, null) },
  lockRuleDisabled: { label: /* @__PURE__ */ React6.createElement(LockIcon, null) },
  shiftActionDown: { label: /* @__PURE__ */ React6.createElement(ChevronDownIcon, null) },
  shiftActionUp: { label: /* @__PURE__ */ React6.createElement(ChevronUpIcon, null) }
};
var QueryBuilderChakra = getCompatContextProvider({
  controlElements: chakraControlElements,
  translations: chakraTranslations
});
export {
  ChakraActionElement,
  ChakraDragHandle,
  ChakraNotToggle,
  ChakraValueEditor,
  ChakraValueSelector,
  QueryBuilderChakra,
  chakraControlElements,
  chakraTranslations
};
//# sourceMappingURL=react-querybuilder_chakra.mjs.map